import { ethers } from 'ethers'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import {
  EthereumDistribute__factory,
  GochainSwap__factory,
  SimpleERC20Token__factory,
  WOMISwapToOMI__factory,
} from '../types/contracts'
import { braidArrays } from '../utils/braidArrays'
import {
  ChainId,
  ContractName,
  getChainId,
  getContractAddress,
  getContractDeploymentBlock,
} from '../utils/chainData'
import {
  ETHEREUM_CHAIN_ID,
  ethereumProvider,
  GOCHAIN_CHAIN_ID,
  gochainProvider,
} from '../utils/connectors'
import { throwIfUndefined } from '../utils/errors'
import { useMetaMask } from '../utils/metamask'

// Infura rejects eth_getLogs spanning more than 10,000 blocks
const ETHEREUM_LOGS_BLOCK_RANGE = 10000
const ETHEREUM_LOGS_CONCURRENCY = 5
// wOMI swap history only covers roughly the last 30 days of Ethereum blocks
const WOMI_SWAP_LOOKBACK_BLOCKS = 216000
// GoChain swaps come from the swaps API, which the backend indexes about once
// a minute. Swaps newer than that are read from the last ~day of GoChain blocks.
const GOCHAIN_RECENT_SWAP_BLOCKS = 17280
const SWAPS_API_URL = process.env.REACT_APP_API_URL ?? ''

interface ApiSwap {
  swapId: string
  amount: string
  status: string
  swapTransactionHash: string
  swapBlockNumber: number
  feeTransactionHash?: string
}

const fetchWalletSwaps = async (account: string): Promise<ApiSwap[]> => {
  const response = await fetch(`${SWAPS_API_URL}/api/swaps/${account}`)
  if (!response.ok) {
    throw new Error(`Swaps API responded with ${response.status}`)
  }
  return (await response.json()).swaps
}

interface GochainSwap {
  swapId: string
  fromChain: 'ethereum' | 'gochain'
  toChain: 'ethereum' | 'gochain'
  transactionHash: string
  blockNumber: number
  amount: ethers.BigNumber
  // Backend status, undefined for swaps the backend hasn't indexed yet
  status?: string
}

const queryInBlockRanges = async <T,>(
  query: (fromBlock: number, toBlock: number) => Promise<T[]>,
  fromBlock: number,
  toBlock: number
): Promise<T[]> => {
  const ranges: [number, number][] = []
  for (
    let start = fromBlock;
    start <= toBlock;
    start += ETHEREUM_LOGS_BLOCK_RANGE
  ) {
    ranges.push([
      start,
      Math.min(start + ETHEREUM_LOGS_BLOCK_RANGE - 1, toBlock),
    ])
  }
  const results: T[] = []
  for (let i = 0; i < ranges.length; i += ETHEREUM_LOGS_CONCURRENCY) {
    const batch = await Promise.all(
      ranges
        .slice(i, i + ETHEREUM_LOGS_CONCURRENCY)
        .map(([from, to]) => query(from, to))
    )
    batch.forEach((events) => results.push(...events))
  }
  return results
}

export interface SwapData {
  metamaskLibrary: ethers.providers.Web3Provider | undefined
  currentAccount: string
  currentChainId: ChainId
  gochainChainId: ChainId
  ethereumChainId: ChainId

  gochainBalance: ethers.BigNumber
  gochainOmiBalance: ethers.BigNumber

  ethereumBalance: ethers.BigNumber
  ethereumOmiBalance: ethers.BigNumber
  ethereumWomiBalance: ethers.BigNumber

  distributionFee: ethers.BigNumber
  hasUnpaidDistribution: boolean
  unpaidDistributionSwapIds: ethers.BigNumber[]
  gochainMinumimSwapAmount: ethers.BigNumber
  womiMinimumSwapAmount: ethers.BigNumber
  swapItems: SwapItem[]
}
interface SwapItem {
  swapId: string
  fromChain: 'ethereum' | 'gochain'
  toChain: 'ethereum' | 'gochain'
  direction: 'To Ethereum' | 'To Gochain'
  transactionHash: string
  blockNumber: number
  amount: ethers.BigNumber
  swapConfirmations: number
  hasFee: boolean
  hasPaidFee?: boolean
  feePaidConfirmations?: number
  distributed: boolean
}

export const useSwapData = (
  loading: boolean,
  setLoading: (loading: boolean) => void,
  setSwapData: (swapData: SwapData) => void
) => {
  const [swapItems, setSwapItems] = useState<SwapItem[]>([])
  const [distributionFee, setDistributionFee] = useState(
    ethers.BigNumber.from(0)
  )
  const [hasUnpaidDistribution, setHasUnpaidDistribution] =
    useState<boolean>(false)
  const [unpaidDistributionSwapIds, setUnpaidDistributionSwapIds] = useState<
    ethers.BigNumber[]
  >([])
  const [gochainMinumimSwapAmount, setGochainMinumimSwapAmount] = useState(
    ethers.BigNumber.from(0)
  )
  const [womiMinumimSwapAmount, setWomiMinumimSwapAmount] = useState(
    ethers.BigNumber.from(0)
  )
  const [gochainBalance, setGochainBalance] = useState(ethers.BigNumber.from(0))
  const [gochainOmiBalance, setGochainOmiBalance] = useState(
    ethers.BigNumber.from(0)
  )
  const [ethereumBalance, setEthereumBalance] = useState(
    ethers.BigNumber.from(0)
  )
  const [ethereumOmiBalance, setEthereumOmiBalance] = useState(
    ethers.BigNumber.from(0)
  )
  const [ethereumWomiBalance, setEthereumWomiBalance] = useState(
    ethers.BigNumber.from(0)
  )

  const {
    library: _metamaskLibrary,
    chainId: _currentChainId,
    account: _currentAccount,
  } = useMetaMask()
  const metamaskLibrary = throwIfUndefined(_metamaskLibrary)
  const currentAccount = throwIfUndefined(_currentAccount)
  const currentChainId = getChainId(_currentChainId)
  const gochainLibrary = gochainProvider
  const gochainChainId = GOCHAIN_CHAIN_ID
  const ethereumLibrary = ethereumProvider
  const ethereumChainId = ETHEREUM_CHAIN_ID

  useEffect(() => {
    setSwapData({
      metamaskLibrary,
      currentAccount,
      currentChainId,
      gochainBalance,
      gochainOmiBalance,
      ethereumBalance,
      ethereumOmiBalance,
      ethereumWomiBalance,
      gochainChainId,
      ethereumChainId,
      distributionFee: distributionFee,
      hasUnpaidDistribution: hasUnpaidDistribution,
      unpaidDistributionSwapIds: unpaidDistributionSwapIds,
      gochainMinumimSwapAmount,
      womiMinimumSwapAmount: womiMinumimSwapAmount,
      swapItems,
    })
    const timeout = setTimeout(() => setLoading(false), 2000)
    return () => clearTimeout(timeout)
  }, [
    loading,
    setLoading,
    metamaskLibrary,
    currentAccount,
    currentChainId,
    gochainBalance,
    gochainOmiBalance,
    ethereumBalance,
    ethereumOmiBalance,
    ethereumWomiBalance,
    gochainChainId,
    ethereumChainId,
    distributionFee,
    hasUnpaidDistribution,
    unpaidDistributionSwapIds,
    gochainMinumimSwapAmount,
    womiMinumimSwapAmount,
    swapItems,
    setSwapData,
  ])

  useEffect(() => {
    const gochainSwapContract = GochainSwap__factory.connect(
      getContractAddress({
        chainId: gochainChainId,
        contractName: ContractName.SWAP,
      }),
      gochainLibrary
    )
    const ethereumDistributeContract = EthereumDistribute__factory.connect(
      getContractAddress({
        chainId: ethereumChainId,
        contractName: ContractName.DISTRIBUTE,
      }),
      ethereumLibrary
    )

    const fetchGochainSwaps = async (fromBlock: number) =>
      gochainSwapContract.queryFilter(
        gochainSwapContract.filters.Swap(
          null,
          _.isUndefined(currentAccount) ? null : currentAccount,
          null,
          null,
          null,
          null
        ),
        fromBlock
      )

    const gochainOmiContract = SimpleERC20Token__factory.connect(
      getContractAddress({
        chainId: gochainChainId,
        contractName: ContractName.OMI,
      }),
      gochainLibrary
    )

    const ethereumOmiContract = SimpleERC20Token__factory.connect(
      getContractAddress({
        chainId: ethereumChainId,
        contractName: ContractName.OMI,
      }),
      ethereumLibrary
    )

    const ethereumWomiContract = SimpleERC20Token__factory.connect(
      getContractAddress({
        chainId: ethereumChainId,
        contractName: ContractName.WOMI,
      }),
      ethereumLibrary
    )

    const ethereumWomiSwapContract = WOMISwapToOMI__factory.connect(
      getContractAddress({
        chainId: ethereumChainId,
        contractName: ContractName.WOMI_SWAP,
      }),
      ethereumLibrary
    )

    const fetchWomiSwaps = async (latestBlock: number) =>
      queryInBlockRanges(
        (fromBlock, toBlock) =>
          ethereumWomiSwapContract.queryFilter(
            ethereumWomiSwapContract.filters.Swap(
              null,
              _.isUndefined(currentAccount) ? null : currentAccount,
              null,
              null
            ),
            fromBlock,
            toBlock
          ),
        Math.max(
          getContractDeploymentBlock({
            chainId: ethereumChainId,
            contractName: ContractName.WOMI_SWAP,
          }),
          latestBlock - WOMI_SWAP_LOOKBACK_BLOCKS
        ),
        latestBlock
      )

    Promise.all([
      gochainLibrary.getBalance(currentAccount),
      ethereumLibrary.getBalance(currentAccount),
      gochainOmiContract.balanceOf(currentAccount),
      ethereumOmiContract.balanceOf(currentAccount),
      ethereumWomiContract.balanceOf(currentAccount),
      gochainLibrary.getBlockNumber(),
      ethereumLibrary.getBlockNumber(),
      ethereumDistributeContract.fee(),
      gochainSwapContract.minimumSwapAmount(),
      ethereumWomiSwapContract.minimumSwapAmount(),
    ])
      .then(
        async ([
          gochainBalance,
          ethereumBalance,
          gochainOmiBalance,
          ethereumOmiBalance,
          ethereumWomiBalance,
          gochainBlockNumber,
          ethereumBlockNumber,
          ethereumDistributionFee,
          gochainMinumimSwapAmount,
          womiMinumimSwapAmount,
        ]) => {
          // Set balances before fetching the swap history, so a failing history
          // query can't leave them at zero
          unstable_batchedUpdates(() => {
            setGochainBalance(gochainBalance)
            setEthereumBalance(ethereumBalance)
            setGochainOmiBalance(gochainOmiBalance)
            setEthereumOmiBalance(ethereumOmiBalance)
            setEthereumWomiBalance(ethereumWomiBalance)
            setDistributionFee(ethereumDistributionFee)
            setGochainMinumimSwapAmount(gochainMinumimSwapAmount)
            setWomiMinumimSwapAmount(womiMinumimSwapAmount)
          })

          const apiSwaps = await fetchWalletSwaps(currentAccount).catch(
            (error) => {
              console.error('Swaps API unavailable, reading the chain', error)
              return undefined
            }
          )
          const gochainDeploymentBlock = getContractDeploymentBlock({
            chainId: gochainChainId,
            contractName: ContractName.SWAP,
          })
          const [gochainSwapEvents, womiSwapEvents] = await Promise.all([
            fetchGochainSwaps(
              apiSwaps
                ? Math.max(
                    gochainDeploymentBlock,
                    gochainBlockNumber - GOCHAIN_RECENT_SWAP_BLOCKS
                  )
                : gochainDeploymentBlock
            ),
            // wOMI history is display only, it mustn't block GoChain swaps
            // or the fee payment check
            fetchWomiSwaps(ethereumBlockNumber).catch((error) => {
              console.error('Failed to load wOMI swaps', error)
              return []
            }),
          ])

          const indexedSwapIds = new Set(apiSwaps?.map((s) => s.swapId))
          const gochainSwaps = [
            ...(apiSwaps ?? []).map<GochainSwap>((s) => ({
              swapId: s.swapId,
              fromChain: 'gochain',
              toChain: 'ethereum',
              transactionHash: s.swapTransactionHash,
              blockNumber: Number(s.swapBlockNumber),
              amount: ethers.BigNumber.from(s.amount),
              status: s.status,
            })),
            ...gochainSwapEvents
              .filter((e) => !indexedSwapIds.has(e.args.swapId.toString()))
              .map<GochainSwap>((e) => ({
                swapId: e.args.swapId.toString(),
                fromChain:
                  e.args.fromChain === 'gochain' ? 'gochain' : 'ethereum',
                toChain: e.args.toChain === 'gochain' ? 'gochain' : 'ethereum',
                transactionHash: e.transactionHash,
                blockNumber: e.blockNumber,
                amount: e.args.amount,
              })),
          ].sort((a, b) => a.blockNumber - b.blockNumber)
          const feePaidSwapIds = new Set(
            apiSwaps
              ?.filter(
                (s) => s.status === 'distributed' || s.feeTransactionHash
              )
              .map((s) => s.swapId)
          )

          const hasPaidFeeMap: {
            [swapId: string]: boolean
          } = {}
          await Promise.all(
            gochainSwaps.map(async (s) => {
              if (ethereumDistributionFee.eq(0)) {
                return
              }
              // Only swaps the backend hasn't seen a fee for are checked on chain
              if (feePaidSwapIds.has(s.swapId)) {
                hasPaidFeeMap[s.swapId] = true
                return
              }
              const hasPaidFee = await ethereumDistributeContract.getHasPaidFee(
                s.swapId
              )
              if (!hasPaidFee) {
                setHasUnpaidDistribution(true)
                setUnpaidDistributionSwapIds((existing) => [
                  ...existing,
                  ethers.BigNumber.from(s.swapId),
                ])
              }
              hasPaidFeeMap[s.swapId] = hasPaidFee
            })
          )

          const lastTenGochainSwaps = gochainSwaps.slice(
            Math.max(gochainSwaps.length - 10, 0)
          )
          const hasDistributed = await Promise.all(
            lastTenGochainSwaps.map((swap) =>
              swap.status !== undefined
                ? swap.status === 'distributed'
                : ethereumDistributeContract.getHasDistributed(swap.swapId)
            )
          )
          const gochainSwapItems = lastTenGochainSwaps
            .map<SwapItem>((swap, i) => ({
              swapId: swap.swapId,
              fromChain: swap.fromChain,
              toChain: swap.toChain,
              direction:
                swap.toChain === 'ethereum' ? 'To Ethereum' : 'To Gochain',
              transactionHash: swap.transactionHash,
              blockNumber: swap.blockNumber,
              amount: swap.amount,
              swapConfirmations:
                (swap.fromChain === 'gochain'
                  ? gochainBlockNumber
                  : ethereumBlockNumber) - swap.blockNumber,
              hasFee: ethereumDistributionFee.gt(0),
              hasPaidFee: hasPaidFeeMap[swap.swapId],
              distributed: hasDistributed[i],
            }))
            .sort((a, b) => b.blockNumber - a.blockNumber)
          const lastTenWomiSwaps = womiSwapEvents.slice(
            Math.max(womiSwapEvents.length - 10, 0)
          )
          const womiSwapItems = lastTenWomiSwaps
            .map<SwapItem>((swap) => ({
              swapId: swap.args.swapId.toString(),
              fromChain: 'ethereum',
              toChain: 'ethereum',
              direction: 'To Ethereum',
              transactionHash: swap.transactionHash,
              blockNumber: swap.blockNumber,
              amount: swap.args.amount,
              swapConfirmations: ethereumBlockNumber - swap.blockNumber,
              hasFee: ethereumDistributionFee.gt(0),
              hasPaidFee: hasPaidFeeMap[swap.args.swapId.toString()],
              // wOMI swaps complete on Ethereum, there is no distribution
              distributed: false,
            }))
            .sort((a, b) => b.blockNumber - a.blockNumber)

          setSwapItems(braidArrays(womiSwapItems, gochainSwapItems)) // This option to avoid having to get the block timestamp for every swap
        }
      )
      .catch((error) => console.error('Failed to load swap data', error))
    // Ensure this runs only once
  }, [
    currentAccount,
    ethereumChainId,
    ethereumLibrary,
    gochainChainId,
    gochainLibrary,
    currentChainId,
  ])
}
